<template>
  <div v-if="vectorLayers.length > 0">
    <v-overlay :value="overlay">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-divider class="mb-4" />
    <div>
      <meteogram-button
        v-if="isMeteogram"
        :meteogram-enabled="meteogramEnabled"
        :location-name="map.locationName"
        :meteogram-obj="meteogramObj"
        :map-enabled="mapEnabled"
        @toggle-meteogram-btn="$emit('toggle-meteogram-btn', $event)"
        @toggle-map-btn="toggleMapBtn(selectedVectorLayer)"
      />
    </div>
    <div
      v-if="showSelect"
      class="mt-4"
    >
      <div class="d-flex">
        <strong>
          {{ $t('ServiceCard.vectorLayer') }}
        </strong>
        <v-spacer />
        <div class="d-flex">
          <layer-opacity-menu
            :key="map.renderOpacityMenu[serviceCardId]"
            :service-card-id="serviceCardId"
            :initial-opacity="getLayerInitialOpacity(selectedVectorLayer.layer_opacity)"
          />
          <v-btn
            v-if="$route.params.id !== 'Archive'"
            outlined
            icon
            small
            color="#505050"
            class="ml-2"
            :disabled="isRefreshing || refreshBtnDisabled"
            @click="clearRefreshInterval(serviceCardId); initRefresh(); setRefreshInterval()"
          >
            <v-icon
              :class="rotateEffect"
              color="#505050"
            >
              mdi-refresh
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div
        v-if="$route.params.id === 'Real-Time' || $route.params.id === 'Forecast'"
        class="d-flex align-center mt-2"
      >
        <v-select
          ref="vectorSelect"
          v-model="selectedVectorLayer"
          :disabled="map.disableAnimationPlay || isRefreshing"
          :attach="`.card${serviceCardId}`"
          :items="vectorLayers.filter(item => !item.layer_name.includes('ignore'))"
          :item-text="getLayerLabel"
          item-color="RMprimary"
          color="RMprimary"
          return-object
          hide-details
          outlined
          :append-icon="isSelectVoid() ? 'mdi-menu-down' : 'mdi-close'"
          :class="[`card${serviceCardId}`, !isSelectVoid() && 'no-rotate-icon']"
          @change="$emit('reset-min-max-step'); openVectorLayer(selectedVectorLayer); googleTag();"
          @click:append="handleAppendIconClick()"
        />
      </div>
      <div
        v-else
        class="d-flex align-center mt-2"
      >
        <v-select
          ref="vectorSelect"
          v-model="selectedVectorLayer"
          :disabled="map.disableAnimationPlay"
          :attach="`.card${serviceCardId}`"
          :items="vectorLayers.filter(item => !item.layer_name.includes('ignore'))"
          :item-text="getLayerLabel"
          item-color="RMprimary"
          color="RMprimary"
          return-object
          hide-details
          outlined
          :append-icon="isSelectVoid() ? 'mdi-menu-down' : 'mdi-close'"
          :class="[`card${serviceCardId}`, !isSelectVoid() && 'no-rotate-icon']"
          @click:append="handleAppendIconClick()"
        />
      </div>
      <!-- TODO: Aggiungere un if per mostrare solo se la selected ha selezionato qualcosa-->
      <div v-if="$route.params.id === 'Archive' && !overlay">
        <service-card-archive
          v-if="renderArchiveDate"
          :selected-vector-layer="selectedVectorLayer"
          :vector-layers="vectorLayers"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :is-table="isTable"
          :is-graph="isGraph"
          :service-name="serviceName"
          :layer-type="'VECTOR'"
          :animation-time-array="animationTimeArray"
          :animation-start-index="animationStartIndex"
          :animation-empty-steps="animationEmptySteps"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          :table-chart-range-date="tableChartRangeDate"
          @set-animation-steps="setAnimationSteps($event)"
          @closeAllOtherServices="$emit('closeAllOtherServices', $event)"
          @activatedNewArchiveService="$emit('activatedNewArchiveService')"
          @changedServiceLayer="$emit('changedServiceLayer')"
          @no-vector-features-found="noVectorFeaturesFound($event)"
          @google-tag="googleTag()"
        />
      </div>
      <div v-if="$route.params.id === 'Forecast' && renderPlayer">
        <service-card-forecast
          :selected-vector-layer="selectedVectorLayer"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :pause-animation="pauseAnimation"
          :overlay="overlay"
          :service-name="serviceName"
          :layer-type="'VECTOR'"
          :animation-time-array="animationTimeArray"
          :animation-start-index="animationStartIndex"
          :animation-empty-steps="animationEmptySteps"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          :table-chart-range-date="tableChartRangeDate"
          :check-policy-limits="checkPolicyLimits"
          @set-animation-steps="setAnimationSteps($event)"
          @reset-min-max-step="$emit('reset-min-max-step')"
          @no-vector-features-found="noVectorFeaturesFound($event)"
        />
      </div>
      <div v-if="$route.params.id === 'Real-Time' && !mobileBreakpoints()">
        <animation-player
          v-if="renderPlayer"
          :selected-vector-layer="selectedVectorLayer"
          :service-card-id="serviceCardId"
          :animation-limit-range-array="animationLimitRangeArray"
          :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
          :animation-step-date-type="animationStepDateType"
          :animation-step="animationStep"
          :min-step="minStep"
          :max-step="maxStep"
          :is-animation="isAnimation"
          :pause-animation="pauseAnimation"
          :overlay="overlay"
          :service-name="serviceName"
          :layer-type="'VECTOR'"
          :animation-time-array="animationTimeArray"
          :animation-start-index="animationStartIndex"
          :animation-empty-steps="animationEmptySteps"
          :no-features-found="noFeaturesFound"
          :no-wmts-features-found="noWmtsFeaturesFound"
          @no-vector-features-found="noVectorFeaturesFound($event)"
        />
      </div>
      <vector-layer-popup
        v-if="($route.params.id === 'Forecast' || $route.params.id === 'Real-Time')"
        :selected-vector-layer="selectedVectorLayer"
        :vector-layers="vectorLayers"
        :service-card-id="serviceCardId"
        :animation-step-date-type="animationStepDateType"
        :animation-step="animationStep"
        :min-step="minStep"
        :max-step="maxStep"
        :service-name="serviceName"
        :is-table="isTable"
        :is-animation="isAnimation"
        :table-chart-range-date="tableChartRangeDate"
        :is-daily-data="dailyData"
        :is-refreshing-data="map.isRefreshingDataServiceID"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { toLonLat } from 'ol/proj';
import { getFormattedDate, updatedDateWithTimeStep, timeToMilliseconds, dateUnit, formatLayerLabel } from '@/utils.js';

export default {
  components: {
    ServiceCardForecast: () => import('@/components/layout/ServiceCardForecast'),
    AnimationPlayer: () => import('@/components/utils/AnimationPlayer'),
    ServiceCardArchive: () => import('@/components/layout/ServiceCardArchive'),
    VectorLayerPopup: () => import('@/components/utils/VectorLayerPopup'),
    MeteogramButton: () => import('@/components/utils/MeteogramButton'),
    LayerOpacityMenu: () => import('@/components/utils/LayerOpacityMenu.vue')
  },
  props: {
    serviceCardId: {
      type: String,
      default: ''
    },
    vectorLayers: {
      type: Array,
      default: () => []
    },
    layerToSelectVector: {
      type: Object,
      default: () => {}
    },
    animationStepDateType: {
      type: String,
      default: ''
    },
    animationStep: {
      type: String,
      default: ''
    },
    animationLimitDateType: {
      type: String,
      default: ''
    },
    animationLimitFuture: {
      type: String,
      default: ''
    },
    animationLimitPast: {
      type: String,
      default: ''
    },
    refreshIntervalStep: {
      type: String,
      default: ''
    },
    refreshIntervalStepType: {
      type: String,
      default: ''
    },
    serviceName: {
      type: String,
      default: ''
    },
    isAnimation: {
      type: Boolean,
      default: false
    },
    isTable: {
      type: Boolean,
      default: false
    },
    isGraph: {
      type: Boolean,
      default: false
    },
    isMeteogram: {
      type: Boolean,
      default: false
    },
    meteogramEnabled: {
      type: Boolean,
      default: false
    },
    meteogramObj: {
      type: Object,
      default: () => {}
    },
    minStep: {
      type: Date,
      default: null
    },
    maxStep: {
      type: Date,
      default: null
    },
    rasterLayers: {
      type: Array,
      default: () => []
    },
    animationLimitRangeArray: {
      type: Array,
      default: () => []
    },
    animationLimitRangeArrayPolicy: {
      type: Array,
      default: () => []
    },
    policyLimits: {
      type: Object,
      default: () => {}
    },
    checkPolicyLimits: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      overlay: false,
      renderArchiveDate: true,
      isRefreshing: false,
      refreshBtnDisabled: false,
      renderPlayer: false,
      noFeaturesFound: false,
      noWmtsFeaturesFound: false,
      mapEnabled: false,
      pauseAnimation: false,
      selectedVectorLayer: {},
      animationTimeArray: [],
      animationEmptySteps: [],
      isProcessingLayers: [],
      animationStartIndex: 0,
      tableChartRangeDate: '',
      rotateEffect: ''
    };
  },
  computed: {
    ...mapState(['map', 'wsServices', 'app_user', 'table_chart', 'geoserver_data']),
    dailyData() {
      return this.animationStepDateType === 'dynamic_months' || this.animationStepDateType === 'dynamic_days' || (this.animationStepDateType === 'dynamic_hours' && this.animationStep >= 24);
    },
    skipWmtsCalls() {
      return this.$route.params.id === 'Archive' && this.selectedVectorLayer.skip_wmts_calls;
    },
    showSelect() {
      return this.mapEnabled || !this.isMeteogram;
    }
  },
  watch: {
    $route() {},
    layerToSelectVector: {
      async handler(newValue) {
        if (this.isProcessingLayers.includes(newValue.layer_label)) {
          return;
        }

        this.isProcessingLayers.push(newValue.layer_label);

        try {
          // Logica esistente
          if (newValue && Object.keys(newValue).length !== 0 && this.checkPolicyLimits) {
            const isDefaultService = this.app_user.defaultService.GLOBAL?.service_type === 'WS' &&
              this.selectedVectorLayer.service_id !== this.layerToSelectVector.service_id &&
              this.app_user.defaultService.GLOBAL?.service_details.some(item => item.service_id === this.serviceCardId);

            const fromRoute = this.$route.params.id.replace('-', '');
            const isSpecificDefaultService = this.app_user.defaultService[fromRoute]?.service_type &&
              this.selectedVectorLayer.service_id !== this.layerToSelectVector.service_id &&
              this.app_user.defaultService[fromRoute].service_details.some(item => item.service_id === this.serviceCardId) &&
              this.map.specificDefaultService[fromRoute];

            const isSingleLayer = this.vectorLayers.filter(item => !item.layer_name.includes('ignore')).length === 1;
            const isAlredyActive = this.wsServices.activeServices.find(s => s.layer_name === newValue.layer_name && s.service_id === newValue.service_id);

            if (!isAlredyActive && (isDefaultService || isSpecificDefaultService || isSingleLayer)) {
              this.selectedVectorLayer = this.layerToSelectVector;

              if (isSpecificDefaultService && this.app_user.defaultService[fromRoute].service_details.some(item => item.service_id === this.serviceCardId && item.is_meteogram)) {
                this.$emit('toggle-meteogram-btn', true);
                this.$emit('check-specific-default-service');
              }

              if (Object.keys(this.selectedVectorLayer).length !== 0 &&
                !this.map.OLmap.getLayers().getArray().slice(1).some(layer => layer.values_.service_id === this.layerToSelectVector.service_id) &&
                this.app_user.defaultService.GLOBAL?.service_path !== 'map/Archive' &&
                this.$route.params.id !== 'Archive') {
                this.mapEnabled = true;
                await this.openVectorLayer(this.selectedVectorLayer);

                if (isSpecificDefaultService) {
                  this.$emit('check-specific-default-service');
                }
              }
            }
          }
        } finally {
          const i = this.isProcessingLayers.indexOf(newValue.layer_label);
          this.isProcessingLayers.splice(i, 1);
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['addActiveService', 'removeActiveService', 'removeEveryLayer', 'removeLayer', 'createVectorLayer', 'checkOpacityServiceID', 'resetAnimationTime', 'storeWsServices', 'getWmtsArray', 'checkNewData', 'removeLayerByServiceID', 'getLayerExtent', 'getWmtsGDV']),
    getLayerLabel(layer) {
      return formatLayerLabel(
        this.$t(`${layer.ws_name}:${layer.layer_name}_serviceID:${this.serviceCardId}.label`),
        layer.udm
      );
    },
    async openVectorLayer(vector) {
      if (!this.isRefreshing) {
        if (!(vector.service_id in this.map.refreshIntervalID)) {
          this.setRefreshInterval();
        }
      }

      this.removePreviousLayer();

      this.renderPlayer = false;
      this.$nextTick(() => {
        this.renderPlayer = true;
      });
      this.overlay = true;
      await this.setAnimationSteps();

      if (!this.isRefreshing && this.map.isAnimating.length == 0) {
        this.resetAnimationTime();
      }

      if (this.mobileBreakpoints()) {
        this.$emit('closeAllOtherServices', this.serviceCardId);

        const service = {
          service_id: this.serviceCardId,
          service_type: 'VECTOR',
          service_name: this.serviceName,
          layer_name: vector.layer_name,
          daily_data: this.dailyData,
          is_animation: this.isAnimation,
          aggregation_interval: vector.aggregation_interval,
          udm: vector.udm,
          udm_conversions: vector.udm_conversions,
          animation_time_array: null,
          animation_start_index: this.animationStartIndex,
          animation_step_date_type: this.animationStepDateType,
          animation_empty_steps: this.animationEmptySteps,
          ws_name: vector.ws_name
        };

        this.addActiveService(service);
      }

      if (this.animationTimeArray.length > 0) {
        this.map.sliderOpacityValue = vector.layer_opacity;

        const extent = await this.getLayerExtent([vector.ws_name, vector.layer_name, vector.layer_type, vector.service_id]);
        const vectorParams = {
          params: {
            GROUP: this.$route.params.id,
            LAYERS: `${vector.ws_name}:${vector.layer_name}`,
            TIME: this.animationTimeArray[this.animationStartIndex],
            STYLES: vector.layer_style,
            USERNAME: this.app_user.username,
            SERVICE_NAME: this.serviceName,
            PARAM_RANGE: this.map.vectorParamRange[this.serviceCardId]
          },
          TYPE: 'VECTOR',
          LAYER_SERVICE_ID: this.serviceCardId,
          UDM: vector.udm,
          UDM_CONVERSIONS: vector.udm_conversions,
          EXTENT: extent,
          AGGREGATION_INTERVAL: vector.aggregation_interval ?? null
        };

        this.vectorLayers.forEach(layer => {
          if (layer.layer_name.includes('ignore')) {
            const index = this.table_chart.completeVectorChartItems.findIndex(item => item.service_id === layer.service_id);
            const obj = {
              layer_name: layer.layer_name,
              ws_name: layer.ws_name,
              service_id: layer.service_id,
              service_name: this.serviceName,
              udm: layer.udm,
              udm_conversions: layer.udm_conversions,
              policy_layers: this.vectorLayers,
              minStep: this.minStep,
              maxStep: this.maxStep,
              isGraph: layer.is_graph,
              isTable: this.isTable,
              animationStep: this.animationStep,
              animationStepDateType: this.animationStepDateType
            };

            this.table_chart.completeVectorChartItems.some(item => item.service_id === layer.service_id) ?
              this.table_chart.completeVectorChartItems[index] = obj
              :
              this.table_chart.completeVectorChartItems.push(obj);
          }
        });

        this.checkOpacityServiceID(this.serviceCardId);

        await this.removeLayerByServiceID(this.serviceCardId);
        await this.createVectorLayer(vectorParams);

        if (this.mobileBreakpoints()) {
          const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

          if (activeService.length > 0) {
            activeService[0].animation_time_array = this.animationTimeArray;
            activeService[0].animation_start_index = this.animationStartIndex;
            this.wsServices.updateStep = true;
          }
        }

        if (this.$route.params.id === 'Forecast') {
          this.renderPlayer = false;
          this.$nextTick(() => {
            this.renderPlayer = true;
          });
        }

        const unit = dateUnit(this.animationStepDateType);
        const centerCoordinates = toLonLat(this.map.OLmap.getView().getCenter());
        const timeStamp = Date.now();
        this.$gtag.event('Intervallo temporale', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id} - ${this.serviceName}`,
          event_label: `${timeStamp} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${getFormattedDate(new Date(vectorParams.params.TIME.split('/')[0]), true)} - ${getFormattedDate(new Date(vectorParams.params.TIME.split('/')[1]), true)} - MIN: ${getFormattedDate(new Date(this.minStep), true)} MAX: ${getFormattedDate(new Date(this.maxStep), true)} STEP: ${this.animationStep}${unit}`
        });
        this.$gtag.event('Centroide mappa', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id} - Lat ${centerCoordinates[1].toFixed(4)} - Lon ${centerCoordinates[0].toFixed(4)}`,
          event_label: `${timeStamp} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${this.serviceName}: ${this.selectedVectorLayer.layer_name} - ${getFormattedDate(new Date(vectorParams.params.TIME.split('/')[0]), true)} - ${getFormattedDate(new Date(vectorParams.params.TIME.split('/')[1]), true)}`
        });
      } else if (this.mobileBreakpoints()) {
        this.$emit('closeAllOtherServices', this.serviceCardId);
      }

      if (!this.isRefreshing) {
        this.reloadLegend();
      }

      this.overlay = false;
    },
    reloadLegend() {
      this.map.reloadLegend = true;
      this.$nextTick(() => {
        this.map.reloadLegend = false;
      });
    },
    async removePreviousLayer() {
      await this.removeActiveService(this.serviceCardId);
      await this.removeLayerByServiceID(this.serviceCardId);
    },
    async removeVectorLayer(vector) {
      this.renderArchiveDate = false;
      this.$nextTick(() => {
        this.renderArchiveDate = true;
      });

      if (this.$route.params.id === 'Forecast' || this.$route.params.id === 'Real-Time') {
        if (Object.keys(this.map.refreshIntervalID).includes(vector.service_id)) {
          this.clearRefreshInterval(vector.service_id);
        }
      }

      this.selectedVectorLayer = {};
      await this.removeLayer(`${vector.ws_name}:${vector.layer_name}`);
      this.reloadLegend();
    },
    async initRefresh() {
      this.isRefreshing = true;
      this.rotateEffect = 'rotating';
      this.refreshBtnDisabled = true;
      setTimeout(() => {
        this.rotateEffect = '';
        this.refreshBtnDisabled = false;
      }, 2000);

      this.$emit('set-refresh-interval-overlay', true);

      const currentLayer = {
        policy_id: this.selectedVectorLayer.policy_id,
        service_id: this.selectedVectorLayer.service_id,
        service_type: 'WS'
      };
      await this.storeWsServices(currentLayer);
      const hasNewAnimationTimestep =
        this.wsServices.wsAppUserPolicy[0].animation_step !== this.animationStep
        || this.wsServices.wsAppUserPolicy[0].animation_step_date_type !== this.animationStepDateType;

      if (hasNewAnimationTimestep) {
        this.$emit('updateWorkspace', this.wsServices.wsAppUserPolicy[0]);
      }

      if (this.animationTimeArray.length > 0) {
        if (this.policyLimits.type != 'static') {
          this.$emit('reset-min-max-step');
        }

        if (this.skipWmtsCalls) {
          if (hasNewAnimationTimestep) {
            await this.refreshVectorLayer();
          }

          this.geoserver_data.isNewData = [];
          this.isRefreshing = false;
          this.$emit('set-refresh-interval-overlay', false);

          return;
        }

        const index = this.$route.params.id === 'Forecast' ? 0 : 1;
        await this.checkNewData(['time', `${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`, this.animationTimeArray.at(-1).split('/')[index]]);

        const [isNewData, newDataTime] = this.geoserver_data.isNewData;

        if (isNewData && this.policyLimits.type !== 'static') {
          this.$emit('changeAnimationLimitRangeArray');
          await this.$nextTick();
        }

        const limit = this.animationLimitRangeArrayPolicy.at(-1);

        if (isNewData && new Date(newDataTime) <= new Date(limit)) {
          await this.refreshVectorLayer();
        } else if (hasNewAnimationTimestep) {
          await this.refreshVectorLayer();
        }
      } else {
        await this.refreshVectorLayer();
      }

      this.geoserver_data.isNewData = [];
      this.isRefreshing = false;
      this.$emit('set-refresh-interval-overlay', false);
    },
    async refreshVectorLayer() {
      if (!this.map.isRefreshingDataServiceID.includes(this.selectedVectorLayer.service_id)) {
        this.map.isRefreshingDataServiceID.push(this.selectedVectorLayer.service_id);
      }

      if (this.map.isAnimating.includes(this.serviceCardId) && this.animationTimeArray.length > 0) {
        this.pauseAnimation = true;
        this.wsServices.pauseAnimation = true;
        await this.setAnimationSteps();
        this.wsServices.pauseAnimation = false;
        this.pauseAnimation = false;
      } else {
        await this.openVectorLayer(this.selectedVectorLayer);
      }

      this.map.isRefreshingDataServiceID = this.map.isRefreshingDataServiceID.filter(id => id !== this.selectedVectorLayer.service_id);
    },
    clearRefreshInterval(id) {
      clearInterval(this.map.refreshIntervalID[id]);
      delete this.map.refreshIntervalID[id];
    },
    setRefreshInterval() {
      const interval = timeToMilliseconds(this.refreshIntervalStep, this.refreshIntervalStepType);

      if (interval > 0) {
        this.map.refreshIntervalID[this.serviceCardId] = setInterval(async () => {
          await this.initRefresh();
        }, interval);
      }
    },
    setAnimationStepsWithoutWmts(wmtsTimeRange, timeStepMs) {
      if (timeStepMs <= 0) {
        this.animationTimeArray = [];

        return;
      }

      let lowerBound = new Date(wmtsTimeRange.split('/')[0]).getTime();
      const upperBound = new Date(wmtsTimeRange.split('/')[1]).getTime();
      const originalLowerBound = lowerBound;
      const animationTimeArray = [];

      if (!this.dailyData) {
        const previousBound = updatedDateWithTimeStep(
          this.animationStep,
          this.animationStepDateType,
          'remove_step',
          lowerBound
        );
        animationTimeArray.push(
          `${new Date(previousBound).toISOString()}/${new Date(lowerBound).toISOString()}`
        );
      }

      while (lowerBound < upperBound) {
        const nextBound = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, 'add_step', lowerBound);
        const boundedUpper = Math.min(nextBound, upperBound);
        animationTimeArray.push(
          `${new Date(lowerBound).toISOString()}/${new Date(boundedUpper).toISOString()}`
        );
        lowerBound = nextBound;
      }

      if (animationTimeArray.length === 0) {
        animationTimeArray.push(wmtsTimeRange);
      }

      this.animationStartIndex = 0;
      this.animationTimeArray = animationTimeArray;
      this.animationEmptySteps = [];

      const tableChartStartTime = updatedDateWithTimeStep(1, 'dynamic_minutes', 'remove_step', originalLowerBound);
      this.tableChartRangeDate = `${new Date(tableChartStartTime).toISOString()}/${new Date(upperBound).toISOString()}`;
      this.table_chart.tableChartRangeDate[this.serviceCardId] = this.tableChartRangeDate;

      const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

      if (activeService.length > 0) {
        activeService[0].animation_time_array = this.animationTimeArray;
        activeService[0].animation_start_index = this.animationStartIndex;
        activeService[0].animation_empty_steps = this.animationEmptySteps;
      }
    },
    async setAnimationSteps(userRangeDates) {
      const [archiveForecastInitialDate, archiveFinalDate] = userRangeDates || [];
      let isWmts = true;
      let lastDataFounded = '';
      this.noWmtsFeaturesFound = this.wsServices.alertNoWMTSFeature = false;

      const tmpAnimationTimeArray = [];
      const nowDate = new Date();
      const startTime = archiveForecastInitialDate || nowDate;
      const step = this.$route.params.id === 'Real-Time' ? this.animationLimitPast : this.animationLimitFuture;
      const realtimeForecastStepDate = updatedDateWithTimeStep(step, this.animationLimitDateType, this.$route.params.id, nowDate);
      const realtimeForecastFinalDate = this.$route.params.id === 'Real-Time'
        ? Math.max(realtimeForecastStepDate, this.checkPolicyLimits.pastRangeTime)
        : Math.min(realtimeForecastStepDate, this.checkPolicyLimits.futureRangeTime);
      const limitDate = this.$route.params.id === 'Archive' ? archiveFinalDate : realtimeForecastFinalDate;
      let limitDateForecast = realtimeForecastFinalDate;
      let startTimeRounded = this.$route.params.id === 'Forecast' ? new Date(startTime).setHours(0, 0, 0, 0) : startTime;
      let wmtsTimeRange = '';
      let dateToAdd = '';
      let rangeTime = '';

      const timeStepMs = timeToMilliseconds(this.animationStep, this.animationStepDateType);

      while (startTimeRounded < startTime && Math.abs(startTimeRounded - startTime) >= timeStepMs) {
        startTimeRounded = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, 'add_step', startTimeRounded);
      }

      if (this.$route.params.id === 'Archive' && startTimeRounded < startTime) {
        startTimeRounded = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, 'add_step', startTimeRounded);
      }

      if (this.$route.params.id === 'Archive') {
        wmtsTimeRange = `${new Date(archiveForecastInitialDate).toISOString()}/${new Date(archiveFinalDate).toISOString()}`;

        if (this.skipWmtsCalls) {
          this.setAnimationStepsWithoutWmts(wmtsTimeRange, timeStepMs);

          return;
        }

        const wmtsParams = {
          layer: `${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`,
          time: wmtsTimeRange,
          service_id: this.selectedVectorLayer.service_id,
          policy_id: this.selectedVectorLayer.policy_id
        };
        await this.getWmtsArray(wmtsParams);

        const xml_document = (new window.DOMParser()).parseFromString(this.geoserver_data.xml_string, 'text/xml');
        const xml_content = xml_document.getElementsByTagName('Domain')[0].textContent;
        const wmtsResultArray = xml_content ? xml_content.split(',') : [];

        if (wmtsResultArray.length > 0) {
          isWmts = true;

          if (!this.dailyData) {
            rangeTime = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, 'remove_step', startTimeRounded);
            tmpAnimationTimeArray.push(`${new Date(rangeTime).toISOString()}/${new Date(startTimeRounded).toISOString()}`);
          }

          dateToAdd = new Date(startTimeRounded).getTime();

          const [lowerBound, upperBound] = wmtsResultArray[0].split('--');
          const tableChartStartTime = updatedDateWithTimeStep(1, 'dynamic_minutes', 'remove_step', lowerBound);
          this.tableChartRangeDate = `${new Date(tableChartStartTime).toISOString()}/${upperBound}`;
        } else {
          isWmts = false;
          this.tableChartRangeDate = null;
          this.noWmtsFeaturesFound = this.wsServices.alertNoWMTSFeature = true;
        }
      } else {
        wmtsTimeRange = this.$route.params.id === 'Real-Time'
          ? `${new Date(limitDate).toISOString()}/${new Date(startTimeRounded).toISOString()}`
          : `${new Date(startTimeRounded).toISOString()}/${new Date(limitDate).toISOString()}`;

        const wmtsParams = {
          layer: `${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`,
          time: wmtsTimeRange,
          service_id: this.selectedVectorLayer.service_id,
          policy_id: this.selectedVectorLayer.policy_id
        };
        await this.getWmtsArray(wmtsParams);

        const xml_document = (new window.DOMParser()).parseFromString(this.geoserver_data.xml_string, 'text/xml');
        const xml_content = xml_document.getElementsByTagName('Domain')[0].textContent;
        const wmtsResultArray = xml_content ? xml_content.split(',') : [];

        if (wmtsResultArray.length > 0) {
          isWmts = true;
          lastDataFounded = this.$route.params.id === 'Real-Time' ? wmtsResultArray[0].split('--')[0] : '';
          const singleData = wmtsResultArray.length === 1;

          const index = this.$route.params.id === 'Forecast' ? 0 : 1;
          const primaryBound = wmtsResultArray[0].split('--')[index];
          limitDateForecast = new Date(wmtsResultArray[0].split('--')[1]).getTime();
          rangeTime = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, startTimeRounded);
          const otherBoundForecast = rangeTime === new Date(primaryBound).getTime() ? new Date(this.maxStep).getTime() : rangeTime;
          const otherBoundRealTime = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, primaryBound);
          tmpAnimationTimeArray.push(this.$route.params.id === 'Real-Time'
            ? `${new Date(otherBoundRealTime).toISOString()}/${primaryBound}`
            : `${primaryBound}/${new Date(otherBoundForecast).toISOString()}`);

          dateToAdd = new Date(primaryBound).getTime();

          const [lowerBound, upperBound] = wmtsResultArray[0].split('--');
          const tableChartStartTime = singleData ? updatedDateWithTimeStep(1, 'dynamic_minutes', 'remove_step', lowerBound) : lowerBound;
          const tableChartEndTime = updatedDateWithTimeStep(this.policyLimits.future, this.policyLimits.type, 'add_step', lowerBound);
          this.tableChartRangeDate = this.$route.params.id === 'Real-Time'
            ? `${new Date(tableChartStartTime).toISOString()}/${upperBound}`
            : `${lowerBound}/${new Date(tableChartEndTime).toISOString()}`;
          this.animationTimeArray = tmpAnimationTimeArray;
        } else {
          isWmts = false;
          this.tableChartRangeDate = null;
          this.noWmtsFeaturesFound = this.wsServices.alertNoWMTSFeature = true;
          this.animationTimeArray = tmpAnimationTimeArray;
          this.removeActiveService(this.serviceCardId);
        }
      }

      if (isWmts) {
        const limitCheckDate = (this.$route.params.id === 'Real-Time' || this.$route.params.id === 'Archive') ? limitDate : limitDateForecast;

        if (lastDataFounded != dateToAdd) {
          lastDataFounded = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, 'remove-step', lastDataFounded);
        }

        while (this.$route.params.id === 'Real-Time' ? (dateToAdd > lastDataFounded) : (dateToAdd < limitCheckDate)) {
          dateToAdd = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, this.$route.params.id, dateToAdd);
          const addORremoveStep = this.$route.params.id === 'Forecast' ? 'add_step' : 'remove_step';
          const baseDate = this.$route.params.id === 'Forecast' && this.dailyData
            ? tmpAnimationTimeArray.at(-1).split('/')[1]
            : dateToAdd;
          rangeTime = updatedDateWithTimeStep(this.animationStep, this.animationStepDateType, addORremoveStep, baseDate);

          if (this.$route.params.id === 'Real-Time' ? (dateToAdd >= limitDate) : (rangeTime <= limitDate)) {
            tmpAnimationTimeArray.push(this.$route.params.id === 'Forecast'
              ? `${new Date(dateToAdd).toISOString()}/${new Date(rangeTime).toISOString()}`
              : `${new Date(rangeTime).toISOString()}/${new Date(dateToAdd).toISOString()}`);
          }
        }

        if (this.$route.params.id === 'Real-Time') {
          this.animationStartIndex = tmpAnimationTimeArray.length - 1;
          this.animationTimeArray = tmpAnimationTimeArray.reverse();
        } else {
          this.animationTimeArray = tmpAnimationTimeArray;
        }

        if (this.$route.params.id === 'Archive' && tmpAnimationTimeArray.length === 0) {
          this.animationTimeArray.push(wmtsTimeRange);
        }

        this.table_chart.tableChartRangeDate[this.serviceCardId] = this.tableChartRangeDate;
        await this.getDataAvailability();
        const activeService = this.wsServices.activeServices.filter(s => s.service_id === this.serviceCardId);

        if (activeService.length > 0) {
          activeService[0].animation_time_array = this.animationTimeArray;
          activeService[0].animation_start_index = this.animationStartIndex;
          activeService[0].animation_empty_steps = this.animationEmptySteps;
        }
      } else {
        this.animationTimeArray = tmpAnimationTimeArray;
      }
    },
    async getDataAvailability() {
      const animationStepLength = this.animationTimeArray.length;

      if (animationStepLength > 0) {
        let time = new Date(this.animationTimeArray[0].split('/')[0]);
        time = time.setTime(time.getTime() - 60000);

        await this.getWmtsGDV([`${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`, new Date(time).toISOString(), animationStepLength]);

        const xml_document = (new window.DOMParser()).parseFromString(this.geoserver_data.xml_string_GDV, 'text/xml');
        const xml_content = xml_document.getElementsByTagName('Domain')[0].textContent;
        let wmtsResultArray = xml_content ? xml_content.split(',') : [];

        const max = wmtsResultArray.find(time => new Date(time).getTime() > new Date(this.animationTimeArray[animationStepLength - 1].split('/')[1]).getTime());

        if (max) {
          const index = wmtsResultArray.indexOf(max);
          wmtsResultArray = wmtsResultArray.slice(0, index);
        }

        const checkStepIsEmpty = (lowerBound, upperBound, wmtsResultArray) => {
          return !wmtsResultArray.some(item => {
            const time = new Date(item).getTime();

            if (this.$route.params.id === 'Forecast') {
              return (lowerBound <= time) && (upperBound > time);
            }

            return (lowerBound < time) && (upperBound >= time);
          });
        };

        this.animationEmptySteps = [];

        if (animationStepLength !== wmtsResultArray.length) {
          this.animationTimeArray.forEach(step => {
            const lowerBound = new Date(step.split('/')[0]).getTime();
            const upperBound = new Date(step.split('/')[1]).getTime();

            if (checkStepIsEmpty(lowerBound, upperBound, wmtsResultArray)) {
              this.animationEmptySteps.push(step);
            }
          });
        }

        this.noVectorFeaturesFound(this.animationEmptySteps.includes(this.animationTimeArray[this.animationStartIndex]));
      }
    },
    noVectorFeaturesFound(bool) {
      this.noFeaturesFound = bool;
      this.wsServices.alertNoFeature = bool;
    },
    toggleMapBtn(selectedVectorLayer) {
      this.mapEnabled = !this.mapEnabled;

      if (!this.mapEnabled) {
        this.removeVectorLayer(selectedVectorLayer);
        this.renderPlayer = false;
      }
    },
    googleTag() {
      if (this.selectedVectorLayer) {
        this.$gtag.event('Variabile', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id}`,
          event_label: `${Date.now()} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: `${this.serviceName}: ${this.selectedVectorLayer.layer_name}`
        });
      }
    },
    isSelectVoid() {
      return Object.keys(this.selectedVectorLayer).length === 0 || this.map.disableAnimationPlay || this.isRefreshing;
    },
    handleAppendIconClick() {
      if (!this.isSelectVoid()) {
        this.removeVectorLayer(this.selectedVectorLayer);
        this.removeActiveService(this.serviceCardId);
        this.renderPlayer = false;
      } else {
        this.$refs.vectorSelect.isMenuActive = true;
      }
    },
    getLayerInitialOpacity(value) {
      if (value === undefined || value === null) {
        return 60;
      }

      return value;
    },
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    }
  }
};
</script>

<style scoped>
.no-rotate-icon /deep/ .v-input__icon--append .v-icon {
  transition: none !important;
  transform: none !important;
}

@-webkit-keyframes rotating /* Safari and Chrome */ {
  from {
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes rotating {
  from {
    -ms-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -ms-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.rotating {
  -webkit-animation: rotating 1s linear infinite;
  -moz-animation: rotating 1s linear infinite;
  -ms-animation: rotating 1s linear infinite;
  -o-animation: rotating 1s linear infinite;
  animation: rotating 1s linear infinite;
}
</style>

